import { ApiFragment } from '../../core/records/entities/api-fragment.class';
import { RecordTypeStructure } from '../../core/records/entities/metadata/metadata.entity';
import { PartialRecord } from '../../core/records/entities/partial-record.class';
import {
    EXTENSION_VIEWER_MAPPING,
    Viewer,
    VIEWERS_USING_ORIGINAL_REPRESENTATION
} from '../entities/viewer-mapping.entity';

// #region Record Viewer Accessibility
export function IsAccessibleInViewer(record: ApiFragment): boolean {
    const isDataType: boolean = record.getRecordStructure() === RecordTypeStructure.Data;

    const isAccessRepresentation: boolean = record.metadata.Administrative?.IsAccess;
    const isOriginalRepresentation: boolean = record.metadata.Administrative?.IsOriginal;

    if (!isDataType && !isAccessRepresentation && !isOriginalRepresentation) {
        return false;
    }

    const isRepresentation: boolean = record.getRecordStructure() === RecordTypeStructure.Representation;
    const extension: string | null = GetExtensionWithMimeTypeFallback(record);

    const notDataOrRepresentation: boolean = !isDataType && !isRepresentation;
    const isDocumentOrUnsupported: boolean =
        !extension || !IsSupportedByViewers(extension) || GetViewerByExtension(extension) === Viewer.PDF;

    // MED-6451: Documents of type record do not generate viewer representations
    if (notDataOrRepresentation && isDocumentOrUnsupported) {
        return false;
    }

    return isDataType ? IsDataTypeAccessibleInViewer(record) : IsRepresentationAccessibleInViewer(record);
}

function IsDataTypeAccessibleInViewer(record: ApiFragment): boolean {
    const hasBrowse: boolean = record.hasPreview();

    if (hasBrowse) {
        // Check support based on PathToPreview extension
        const pathToPreview: string | null = record.metadata.Internal.PathToPreview;
        const previewExtension: string | null = pathToPreview ? GetFileExtension(pathToPreview) : null;

        return previewExtension ? IsSupportedByViewers(previewExtension) : false;
    }

    // In rare cases, like warc files, no browses are generated, but we do support viewing the original
    // Check support based on original extension
    return IsDataTypeOriginalAccessibleInViewer(record);
}

export function IsDataTypeOriginalAccessibleInViewer(record: PartialRecord): boolean {
    const hasPathToOriginal: boolean = !!record.metadata.Internal?.PathToOriginal;

    if (!hasPathToOriginal) {
        return false;
    }

    const originalExtension = GetExtensionWithMimeTypeFallback(record);
    // Only allow if viewer specifically uses original
    return originalExtension ? CheckOriginalRepresentationAllowed(originalExtension) : false;
}

function IsRepresentationAccessibleInViewer(record: ApiFragment): boolean {
    const isOriginalRepresentation: boolean = record.metadata.Administrative.IsOriginal;

    const extension = GetExtensionWithMimeTypeFallback(record);
    const isSupportedByViewers = extension ? IsSupportedByViewers(extension) : false;

    // Only allow for Original Representation if viewer specifically uses original
    if (extension && isOriginalRepresentation && isSupportedByViewers) {
        return CheckOriginalRepresentationAllowed(extension);
    }

    return isSupportedByViewers;
}

function CheckOriginalRepresentationAllowed(extension: string): boolean {
    const viewer: Viewer = GetViewerByExtension(extension);
    return VIEWERS_USING_ORIGINAL_REPRESENTATION.includes(viewer);
}
// #endregion

// #region Viewer support
export function GetViewerByExtension(extension: string): Viewer {
    return EXTENSION_VIEWER_MAPPING[extension] ?? Viewer.NOT_SUPPORTED;
}

export function IsSupportedByViewers(extension: string): boolean {
    return GetViewerByExtension(extension) !== Viewer.NOT_SUPPORTED;
}
// #endregion

// #region Extension
export function GetExtensionWithMimeTypeFallback(record: PartialRecord): string | null {
    return (
        record.metadata.Technical?.OriginalExtension?.toLowerCase() ??
        GetExtensionFromMimeType(record.metadata.Technical?.MimeType)?.toLowerCase() ??
        null
    );
}

function GetExtensionFromMimeType(mimeType: string | undefined | null): string | undefined {
    if (mimeType === undefined) {
        return undefined;
    }
    return mimeType?.split('/')[1];
}

function GetFileExtension(filename: string): string | null {
    const match = filename.match(/\.([^.]+)$/);
    return match ? match[1] : null;
}
// #endregion
